import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Mail, Phone, MapPin, Building, ImagePlus, FilePenLine, Landmark, Loader2, Upload, Settings } from 'lucide-react';
import States from '../../constant/states';

const MAX_FILE_SIZE_KB = 500;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_KB * 1024;

const AccountForm = ({ initialFormData, onSubmit, onCancel, onFileUpload, isSubmitting, userRole }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [logoLoading, setLogoLoading] = useState(false);
    const [signatureLoading, setSignatureLoading] = useState(false);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [logoError, setLogoError] = useState(null);
    const [signatureError, setSignatureError] = useState(null);
    const [dashboardError, setDashboardError] = useState(null);
    const [userAdminView, setUserAdminView] = useState(false);

    const logoInputRef = useRef(null);
    const signatureInputRef = useRef(null);
    const dashboardInputRef = useRef(null);

    const initialStartFrom = initialFormData?.startFrom ?? 0;

    // Generate month options from April 2025 to December 2027
    const monthOptions = useMemo(() => {
        const options = [];
        const startYear = 2025;
        const startMonth = 4; // April
        const endYear = 2027;
        const endMonth = 12; // December

        for (let year = startYear; year <= endYear; year++) {
            const monthStart = year === startYear ? startMonth : 1;
            const monthEnd = year === endYear ? endMonth : 12;
            for (let month = monthStart; month <= monthEnd; month++) {
                const value = `${year}-${month}`;
                const label = new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                options.push({ value, label });
            }
        }
        return options;
    }, []);

    useEffect(() => {
        const isAdmin = localStorage.getItem("role") === "admin";
        const viewingUserId = localStorage.getItem("adminSelectedUser");
        setUserAdminView(isAdmin && !!viewingUserId);

        setFormData(initialFormData);
        setLogoLoading(false);
        setSignatureLoading(false);
        setDashboardLoading(false);
        setLogoError(null);
        setSignatureError(null);
        setDashboardError(null);

        if (logoInputRef.current) logoInputRef.current.value = null;
        if (signatureInputRef.current) signatureInputRef.current.value = null;
        if (dashboardInputRef.current) dashboardInputRef.current.value = null;
    }, [initialFormData]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prevState => ({
            ...prevState,
            [name]: newValue
        }));

        if (name === 'companyPhone') {
            const phoneRegex = /^(?:\+91[\-\s]?)?[6-9]\d{9}$/;
            if (value && !phoneRegex.test(value)) {
                setErrors((prev) => ({ ...prev, companyPhone: 'Invalid Indian mobile number (10 digits, starting 6-9)' }));
            } else {
                setErrors((prev) => ({ ...prev, companyPhone: '' }));
            }
        }

        if (name === 'isGstExempted') {
            if (checked) {
                setFormData(prevState => ({ ...prevState, gst: '' }));
                setErrors(prev => ({ ...prev, gst: '' }));
            }
        }

        if (name === 'gst' && !formData.isGstExempted) {
            const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;
            if (value && !gstRegex.test(value)) {
                setErrors((prev) => ({ ...prev, gst: 'Invalid GST format' }));
            } else {
                setErrors((prev) => ({ ...prev, gst: '' }));
            }
        }
    };

    // Handler for multi-select changes
    const handleMultiSelectChange = (e) => {
        const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
        setFormData(prevState => ({
            ...prevState,
            imsfreezeDateList: selectedValues
        }));
    };

    const handleFileSelection = (e) => {
        const { name, files } = e.target;
        const file = files?.[0];

        let fileType;
        let setError;
        let inputRef;
        let currentFileValue = initialFormData[name];

        switch (name) {
            case 'companyLogo':
                fileType = 'logo';
                setError = setLogoError;
                inputRef = logoInputRef;
                break;
            case 'signature':
                fileType = 'signature';
                setError = setSignatureError;
                inputRef = signatureInputRef;
                break;
            case 'dashboardImage':
                fileType = 'dashboard';
                setError = setDashboardError;
                inputRef = dashboardInputRef;
                break;
            default:
                return;
        }

        setError(null);

        if (!file) {
            if (!(formData[name] instanceof File)) {
                setFormData(prev => ({ ...prev, [name]: currentFileValue }));
            }
            return;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            setError(`File is too large. Max size: ${MAX_FILE_SIZE_KB}KB`);
            if (inputRef.current) inputRef.current.value = null;
            setFormData(prev => ({ ...prev, [name]: currentFileValue }));
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: file
        }));
    };

    const handleLogoUploadClick = async () => {
        const file = formData.companyLogo;
        if (!(file instanceof File)) {
            setLogoError("No new logo file selected to upload.");
            return;
        }
        setLogoLoading(true);
        setLogoError(null);
        try {
            await onFileUpload(file, 'logo');
            if (logoInputRef.current) logoInputRef.current.value = null;
        } catch (uploadError) {
            console.error("Logo upload error in form:", uploadError);
            setLogoError(uploadError.message || "Logo upload failed.");
        } finally {
            setLogoLoading(false);
        }
    };

    const handleSignatureUploadClick = async () => {
        const file = formData.signature;
        if (!(file instanceof File)) {
            setSignatureError("No new signature file selected to upload.");
            return;
        }
        setSignatureLoading(true);
        setSignatureError(null);
        try {
            await onFileUpload(file, 'signature');
            if (signatureInputRef.current) signatureInputRef.current.value = null;
        } catch (uploadError) {
            console.error("Signature upload error in form:", uploadError);
            setSignatureError(uploadError.message || "Signature upload failed.");
        } finally {
            setSignatureLoading(false);
        }
    };

    const handleDashboardUploadClick = async () => {
        const file = formData.dashboardImage;
        if (!(file instanceof File)) {
            setDashboardError("No new dashboard image file selected to upload.");
            return;
        }
        setDashboardLoading(true);
        setDashboardError(null);
        try {
            await onFileUpload(file, 'dashboard');
            if (dashboardInputRef.current) dashboardInputRef.current.value = null;
        } catch (uploadError) {
            console.error("Dashboard image upload error in form:", uploadError);
            setDashboardError(uploadError.message || "Dashboard image upload failed.");
        } finally {
            setDashboardLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (logoLoading || signatureLoading || dashboardLoading) {
            console.warn("Please wait for file uploads to complete.");
            return;
        }

        let formIsValid = true;
        const currentErrors = { ...errors };

        if (!formData.companyName?.trim()) { currentErrors.companyName = 'Company Name is required'; formIsValid = false; } else { currentErrors.companyName = ''; }
        if (!formData.companyPhone?.trim()) { currentErrors.companyPhone = 'Company Phone is required'; formIsValid = false; } else if (!currentErrors.companyPhone) { currentErrors.companyPhone = ''; }
        if (!formData.companyEmail?.trim()) { currentErrors.companyEmail = 'Company Email is required'; formIsValid = false; } else { currentErrors.companyEmail = ''; }
        if (!formData.isGstExempted && !formData.gst?.trim()) { currentErrors.gst = 'GST Number is required'; formIsValid = false; } else if (!formData.isGstExempted && !currentErrors.gst) { currentErrors.gst = ''; } else if (formData.isGstExempted) { currentErrors.gst = ''; }
        if (!formData.streetAddress?.trim()) { currentErrors.streetAddress = 'Street Address is required'; formIsValid = false; } else { currentErrors.streetAddress = ''; }
        if (!formData.city?.trim()) { currentErrors.city = 'City is required'; formIsValid = false; } else { currentErrors.city = ''; }
        if (!formData.state?.trim()) { currentErrors.state = 'State is required'; formIsValid = false; } else { currentErrors.state = ''; }
        if (!formData.pincode?.trim()) { currentErrors.pincode = 'Pincode is required'; formIsValid = false; } else { currentErrors.pincode = ''; }
        if (!formData.bankName?.trim()) { currentErrors.bankName = 'Bank Name is required'; formIsValid = false; } else { currentErrors.bankName = ''; }
        if (!formData.accountName?.trim()) { currentErrors.accountName = 'Account Name is required'; formIsValid = false; } else { currentErrors.accountName = ''; }
        if (!formData.accountNumber?.trim()) { currentErrors.accountNumber = 'Account Number is required'; formIsValid = false; } else { currentErrors.accountNumber = ''; }
        if (!formData.ifsc?.trim()) { currentErrors.ifsc = 'IFSC is required'; formIsValid = false; } else { currentErrors.ifsc = ''; }
        if (formData.startFrom === null || formData.startFrom === undefined || formData.startFrom < 0) { currentErrors.startFrom = 'Start From number is required and must be 0 or greater'; formIsValid = false; } else if (!currentErrors.startFrom) { currentErrors.startFrom = ''; }

        // Validation for imsfreezeDateList
        if (userRole === 'admin' && (!formData.imsfreezeDateList || formData.imsfreezeDateList.length === 0)) {
            currentErrors.imsfreezeDateList = 'At least one IMS Freeze date is required';
            formIsValid = false;
        } else {
            currentErrors.imsfreezeDateList = '';
        }

        if (currentErrors.companyPhone) formIsValid = false;
        if (!formData.isGstExempted && currentErrors.gst) formIsValid = false;
        if (currentErrors.startFrom) formIsValid = false;

        setErrors(currentErrors);

        if (!formIsValid) {
            console.log("Form validation failed", currentErrors);
            return;
        }
        onSubmit(formData);
    };

    const getPreviewUrl = (fileOrUrl) => {
        if (fileOrUrl instanceof File) {
            return URL.createObjectURL(fileOrUrl);
        }
        if (typeof fileOrUrl === 'string' && fileOrUrl) {
            return fileOrUrl;
        }
        return null;
    };

    function formatDateTimeLocal(value) {
        if (!value) return '';
        const date = new Date(value);
        return date.toISOString().slice(0, 10);
    }

    const logoPreviewUrl = getPreviewUrl(formData.companyLogo);
    const signaturePreviewUrl = getPreviewUrl(formData.signature);
    const dashboardPreviewUrl = getPreviewUrl(formData.dashboardImage);

    const isUploading = logoLoading || signatureLoading || dashboardLoading;
    const disableActions = isSubmitting || isUploading;

    return (
        <div className="bg-white rounded-lg shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg">
                {userRole === 'admin' && (
                    <div className="mb-8 border-b pb-8">
                        <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
                            <Settings className="h-5 w-5 mr-2 text-purple-600" />
                            Admin Settings
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="freezePeriodStart" className="block text-sm font-medium text-gray-700 mb-1">
                                    Sales Freeze (Start) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    required
                                    type="date"
                                    id="freezePeriodStart"
                                    name="freezePeriodStart"
                                    value={formatDateTimeLocal(formData.freezePeriodStart) ?? ''}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 border-gray-300 focus:ring-blue-500`}
                                />
                            </div>
                            <div>
                                <label htmlFor="freezePeriodEnd" className="block text-sm font-medium text-gray-700 mb-1">
                                    Sales Freeze (End) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    required
                                    type="date"
                                    id="freezePeriodEnd"
                                    name="freezePeriodEnd"
                                    value={formatDateTimeLocal(formData.freezePeriodEnd) ?? ''}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 border-gray-300 focus:ring-blue-500`}
                                />
                            </div>
                            <div>
                                <label htmlFor="imsfreezeDateList" className="block text-sm font-medium text-gray-700 mb-1">
                                    IMS Freeze <span className="text-red-500">*</span>
                                </label>
                                <select
                                    multiple
                                    id="imsfreezeDateList"
                                    name="imsfreezeDateList"
                                    value={formData.imsfreezeDateList ?? []}
                                    onChange={handleMultiSelectChange}
                                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 border-gray-300 focus:ring-blue-500`}
                                >
                                    {monthOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.imsfreezeDateList && <p className="text-red-500 text-xs mt-1">{errors.imsfreezeDateList}</p>}
                            </div>
                            <div className="md:col-span-1">
                                <label htmlFor="dashboardImage" className="block text-sm font-medium text-gray-700 mb-1">
                                    Dashboard Image (Optional, Max {MAX_FILE_SIZE_KB}KB)
                                </label>
                                <div className="flex items-center space-x-2">
                                    <span className={`inline-flex items-center px-3 rounded-l-md border border-r-0 h-10 ${userAdminView ? 'bg-gray-200' : 'bg-gray-50'} text-gray-500 ${dashboardError ? 'border-red-500' : 'border-gray-300'}`}>
                                        <ImagePlus className="h-4 w-4" />
                                    </span>
                                    <input
                                        ref={dashboardInputRef}
                                        type="file"
                                        id="dashboardImage"
                                        name="dashboardImage"
                                        accept="image/png, image/jpeg, image/jpg, image/gif"
                                        onChange={handleFileSelection}
                                        disabled={dashboardLoading}
                                        className={`flex-1 min-w-0 block w-full text-sm text-gray-500 border rounded-r-md cursor-pointer bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${dashboardError ? 'border-red-500' : 'border-gray-300'} file:mr-4 file:py-2 file:px-4 file:rounded-l-none file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleDashboardUploadClick}
                                        disabled={!(formData.dashboardImage instanceof File) || dashboardLoading}
                                        className="px-3 py-2 border border-purple-600 text-purple-600 text-sm font-medium rounded-md hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                    >
                                        {dashboardLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                        <span className="ml-1.5 hidden sm:inline">{dashboardLoading ? 'Uploading...' : 'Upload'}</span>
                                    </button>
                                </div>
                                {dashboardError && <p className="text-red-500 text-xs mt-1">{dashboardError}</p>}
                                {dashboardPreviewUrl && !dashboardError && (
                                    <img src={dashboardPreviewUrl} alt="Dashboard Preview" className="mt-2 h-16 w-auto rounded border p-1" />
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div className="mb-8 border-b pb-8">
                    <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
                        <Building className="h-5 w-5 mr-2 text-blue-600" />
                        Business Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-1">
                            <label htmlFor="companyLogo" className="block text-sm font-medium text-gray-700 mb-1">
                                Business Logo (Optional, Max {MAX_FILE_SIZE_KB}KB)
                            </label>
                            <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-3 rounded-l-md border border-r-0 h-10 ${userAdminView ? 'bg-gray-200' : 'bg-gray-50'} text-gray-500 ${logoError ? 'border-red-500' : 'border-gray-300'}`}>
                                    <ImagePlus className="h-4 w-4" />
                                </span>
                                <input
                                    ref={logoInputRef}
                                    type="file"
                                    id="companyLogo"
                                    name="companyLogo"
                                    accept="image/png, image/jpeg, image/jpg, image/gif"
                                    onChange={handleFileSelection}
                                    disabled={logoLoading || userAdminView}
                                    className={`flex-1 min-w-0 block w-full text-sm text-gray-500 border rounded-r-md cursor-pointer bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${logoError ? 'border-red-500' : 'border-gray-300'} file:mr-4 file:py-2 file:px-4 file:rounded-l-none file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                <button
                                    type="button"
                                    onClick={handleLogoUploadClick}
                                    disabled={!(formData.companyLogo instanceof File) || logoLoading || userAdminView}
                                    className="px-3 py-2 border border-blue-600 text-blue-600 text-sm font-medium rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                    {logoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    <span className="ml-1.5 hidden sm:inline">{logoLoading ? 'Uploading...' : 'Upload'}</span>
                                </button>
                            </div>
                            {logoError && <p className="text-red-500 text-xs mt-1">{logoError}</p>}
                            {logoPreviewUrl && !logoError && (
                                <img src={logoPreviewUrl} alt="Logo Preview" className="mt-2 h-16 w-auto rounded border p-1" />
                            )}
                        </div>
                        <div className="md:col-span-1">
                            <label htmlFor="signature" className="block text-sm font-medium text-gray-700 mb-1">
                                Signature (Optional, Max {MAX_FILE_SIZE_KB}KB)
                            </label>
                            <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-3 rounded-l-md border border-r-0 h-10 ${userAdminView ? 'bg-gray-200' : 'bg-gray-50'} text-gray-500 ${signatureError ? 'border-red-500' : 'border-gray-300'}`}>
                                    <FilePenLine className="h-4 w-4" />
                                </span>
                                <input
                                    ref={signatureInputRef}
                                    type="file"
                                    id="signature"
                                    name="signature"
                                    accept="image/png, image/jpeg, image/jpg"
                                    onChange={handleFileSelection}
                                    disabled={signatureLoading || userAdminView}
                                    className={`flex-1 min-w-0 block w-full text-sm text-gray-500 border rounded-r-md cursor-pointer bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${signatureError ? 'border-red-500' : 'border-gray-300'} file:mr-4 file:py-2 file:px-4 file:rounded-l-none file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                <button
                                    type="button"
                                    onClick={handleSignatureUploadClick}
                                    disabled={!(formData.signature instanceof File) || signatureLoading || userAdminView}
                                    className="px-3 py-2 border border-blue-600 text-blue-600 text-sm font-medium rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                    {signatureLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    <span className="ml-1.5 hidden sm:inline">{signatureLoading ? 'Uploading...' : 'Upload'}</span>
                                </button>
                            </div>
                            {signatureError && <p className="text-red-500 text-xs mt-1">{signatureError}</p>}
                            {signaturePreviewUrl && !signatureError && (
                                <img src={signaturePreviewUrl} alt="Signature Preview" className="mt-2 h-16 w-auto rounded border p-1 bg-gray-50" />
                            )}
                        </div>
                        <div>
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                                Company Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                required
                                disabled={userAdminView}
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName ?? ''}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.companyName ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
                        </div>
                        <div>
                            <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700 mb-1">
                                Company Phone <span className="text-red-500">*</span>
                            </label>
                            <div className="flex">
                                <span className={`inline-flex items-center px-3 rounded-l-md border border-r-0 ${userAdminView ? 'bg-gray-200' : 'bg-gray-50'} text-gray-500 ${errors.companyPhone ? 'border-red-500' : 'border-gray-300'}`}>
                                    <Phone className="h-4 w-4" />
                                </span>
                                <input
                                    type="tel"
                                    disabled={userAdminView}
                                    required
                                    id="companyPhone"
                                    name="companyPhone"
                                    value={formData.companyPhone ?? ''}
                                    onChange={handleInputChange}
                                    className={`flex-1 min-w-0 px-4 py-2 border rounded-r-md focus:ring-2 focus:border-blue-500 ${errors.companyPhone ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="e.g., 9876543210"
                                />
                            </div>
                            {errors.companyPhone && <p className="text-red-500 text-xs mt-1">{errors.companyPhone}</p>}
                        </div>
                        <div className="md:col-span-2 flex flex-col md:flex-row md:items-start gap-4">
                            <div className="flex-1">
                                <label htmlFor="companyEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                    Company Email <span className="text-red-500">*</span>
                                </label>
                                <div className="flex">
                                    <span className={`inline-flex items-center px-3 rounded-l-md border border-r-0 ${userAdminView ? 'bg-gray-200' : 'bg-gray-50'} text-gray-500 ${errors.companyEmail ? 'border-red-500' : 'border-gray-300'}`}>
                                        <Mail className="h-4 w-4" />
                                    </span>
                                    <input
                                        type="email"
                                        disabled={userAdminView}
                                        required
                                        id="companyEmail"
                                        name="companyEmail"
                                        value={formData.companyEmail ?? ''}
                                        onChange={handleInputChange}
                                        className={`flex-1 min-w-0 px-4 py-2 border rounded-r-md focus:ring-2 focus:border-blue-500 ${errors.companyEmail ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    />
                                </div>
                                {errors.companyEmail && <p className="text-red-500 text-xs mt-1">{errors.companyEmail}</p>}
                            </div>
                            <div className="flex-1">
                                <label htmlFor="gst" className="block text-sm font-medium text-gray-700 mb-1">
                                    Tax ID/GST Number {!formData.isGstExempted && <span className="text-red-500">*</span>}
                                </label>
                                <input
                                    type="text"
                                    required={!formData.isGstExempted}
                                    id="gst"
                                    name="gst"
                                    value={formData.gst ?? ''}
                                    onChange={handleInputChange}
                                    disabled={formData.isGstExempted || userAdminView}
                                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 ${formData.isGstExempted || userAdminView ? 'bg-gray-100 cursor-not-allowed border-gray-300' : errors.gst ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
                                    maxLength={15}
                                />
                                {errors.gst && !formData.isGstExempted && <p className="text-red-500 text-xs mt-1">{errors.gst}</p>}
                            </div>
                            <div className="flex items-end pb-2 pt-6 md:pt-0 md:self-end">
                                <div className="flex items-center h-10">
                                    <input
                                        disabled={userAdminView}
                                        type="checkbox"
                                        id="isGstExempted"
                                        name="isGstExempted"
                                        checked={!!formData.isGstExempted}
                                        onChange={handleInputChange}
                                        className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${userAdminView ? 'cursor-not-allowed' : ''}`}
                                    />
                                    <label htmlFor="isGstExempted" className={`ml-2 block text-sm font-medium text-gray-700 ${userAdminView ? 'cursor-not-allowed text-gray-500' : ''}`}>
                                        GST Exempted
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mb-8 border-b pb-8">
                    <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
                        <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                        Address Information
                    </h3>
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1">
                                Street Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                disabled={userAdminView}
                                type="text"
                                id="streetAddress"
                                name="streetAddress"
                                required
                                value={formData.streetAddress ?? ''}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.streetAddress ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.streetAddress && <p className="text-red-500 text-xs mt-1">{errors.streetAddress}</p>}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                    City <span className="text-red-500">*</span>
                                </label>
                                <input
                                    disabled={userAdminView}
                                    type="text"
                                    id="city"
                                    name="city"
                                    required
                                    value={formData.city ?? ''}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.city ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                                {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                            </div>
                            <div>
                                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                                    State/Province <span className="text-red-500">*</span>
                                </label>
                                <States
                                    disabled={userAdminView}
                                    value={formData.state ?? ''}
                                    onChange={(e) => handleInputChange({ target: { name: 'state', value: e.target.value, type: 'select-one' } })}
                                    required
                                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} ${errors.state ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
                                />
                                {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="pincode" className="block text-sm font-medium text-gray-700 mb-1">
                                    ZIP/Postal Code <span className="text-red-500">*</span>
                                </label>
                                <input
                                    disabled={userAdminView}
                                    type="text"
                                    id="pincode"
                                    name="pincode"
                                    required
                                    value={formData.pincode ?? ''}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.pincode ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    maxLength={10}
                                />
                                {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
                            </div>
                            <div>
                                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                                    Country <span className="text-red-500">*</span>
                                </label>
                                <input
                                    disabled={userAdminView}
                                    type="text"
                                    id="country"
                                    name="country"
                                    required
                                    value={formData.country ?? 'India'}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100 ${userAdminView ? 'cursor-not-allowed' : ''}`}
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mb-8 border-b pb-8">
                    <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
                        <Landmark className="h-5 w-5 mr-2 text-blue-600" />
                        Bank Details <span className="text-xs text-gray-500 font-normal ml-1">(Required if applicable)</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1">
                                Bank Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                disabled={userAdminView}
                                type="text"
                                id="bankName"
                                name="bankName"
                                required
                                value={formData.bankName ?? ''}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.bankName ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.bankName && <p className="text-red-500 text-xs mt-1">{errors.bankName}</p>}
                        </div>
                        <div>
                            <label htmlFor="accountName" className="block text-sm font-medium text-gray-700 mb-1">
                                Account Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                disabled={userAdminView}
                                type="text"
                                id="accountName"
                                name="accountName"
                                required
                                value={formData.accountName ?? ''}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.accountName ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.accountName && <p className="text-red-500 text-xs mt-1">{errors.accountName}</p>}
                        </div>
                        <div>
                            <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                Account Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                disabled={userAdminView}
                                type="text"
                                id="accountNumber"
                                required
                                name="accountNumber"
                                value={formData.accountNumber ?? ''}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.accountNumber ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.accountNumber && <p className="text-red-500 text-xs mt-1">{errors.accountNumber}</p>}
                        </div>
                        <div>
                            <label htmlFor="ifsc" className="block text-sm font-medium text-gray-700 mb-1">
                                IFSC <span className="text-red-500">*</span>
                            </label>
                            <input
                                disabled={userAdminView}
                                type="text"
                                id="ifsc"
                                name="ifsc"
                                required
                                value={formData.ifsc ?? ''}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.ifsc ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                maxLength={11}
                            />
                            {errors.ifsc && <p className="text-red-500 text-xs mt-1">{errors.ifsc}</p>}
                        </div>
                    </div>
                </div>
                <div className="mb-8 border-b pb-8">
                    <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
                        <Building className="h-5 w-5 mr-2 text-blue-600" />
                        Invoice Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="prefix" className="block text-sm font-medium text-gray-700 mb-1">
                                Prefix (Optional, e.g., INV-)
                            </label>
                            <input
                                disabled={userAdminView}
                                type="text"
                                id="prefix"
                                name="prefix"
                                value={formData.prefix ?? ''}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 focus:ring-blue-500'}`}
                            />
                        </div>
                        <div>
                            <label htmlFor="startFrom" className="block text-sm font-medium text-gray-700 mb-1">
                                Start From (Next Invoice Number) <span className="text-red-500">*</span>
                            </label>
                            <input
                                disabled={userAdminView}
                                type="number"
                                id="startFrom"
                                name="startFrom"
                                value={formData.startFrom ?? 0}
                                onChange={handleInputChange}
                                required
                                min="0"
                                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${errors.startFrom ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.startFrom && <p className="text-red-500 text-xs mt-1">{errors.startFrom}</p>}
                            {/* {formData.startFrom !== undefined && formData.startFrom >= 0 && !errors.startFrom &&
                                <p className="text-xs text-gray-500 mt-1">Your next invoice will be: {formData.prefix ?? ''}-{formData.startFrom + 1}</p>
                            } */}
                        </div>
                    </div>
                </div>
                <div className="mb-8 border-b pb-8">
                    <label htmlFor="terms" className="block text-sm font-medium text-gray-700 mb-1">
                        Terms & Conditions (Optional)
                    </label>
                    <textarea
                        id="terms"
                        disabled={userAdminView}
                        name="terms"
                        value={formData.terms ?? ''}
                        onChange={handleInputChange}
                        rows={4}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:border-blue-500 ${userAdminView ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 focus:ring-blue-500'}`}
                        placeholder="Enter payment terms, warranty information, etc."
                    />
                </div>
                <div className="flex justify-end space-x-4 mt-10 pt-6 border-t">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting || isUploading}
                        className="px-6 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={disableActions}
                        className="px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...
                            </>
                        ) : isUploading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...
                            </>
                        ) : (
                            'Update Profile'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AccountForm;